import { useMutation } from '@tanstack/react-query';
import { getKidSub, useKidToken } from '@/auth/authStore';
import { api } from '@/lib/api';
import { EnglishSharingPanel } from './EnglishSharingPanel';
import { StudyShell } from './StudyShell';

/** Downloads the learner's own Study records as JSON. Nothing is requested until the click. */
async function downloadStudyRecords() {
  const records = await api<Record<string, unknown>>('/study/me/export');
  const url = URL.createObjectURL(
    new Blob([JSON.stringify(records, null, 2)], { type: 'application/json' }),
  );
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = 'my-study-records.json';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Study settings (study-my-year-prd.md §7): My data and Sharing. Term settings stay on Home. */
export function StudySettingsPage() {
  const kidToken = useKidToken();
  const owner = kidToken ? (getKidSub() ?? 'anonymous') : 'anonymous';
  const exportRecords = useMutation({ mutationFn: downloadStudyRecords });
  return (
    <StudyShell>
      <div className="mx-auto w-full max-w-3xl">
        <h1 className="study-page-title text-3xl font-extrabold text-ink sm:text-4xl">Study settings</h1>
        <section aria-labelledby="study-settings-data" className="card-base mt-6">
          <h2 id="study-settings-data" className="text-xl font-extrabold text-ink">
            My data
          </h2>
          <p className="mt-2 text-sm text-ink-soft">
            Download a copy of your plans, tasks and notes list. Original files and Tutor chats are
            not included.
          </p>
          <button
            type="button"
            className="btn-pill-secondary mt-4"
            disabled={exportRecords.isPending}
            onClick={() => exportRecords.mutate()}
          >
            {exportRecords.isPending ? 'Preparing download…' : 'Download my Study records'}
          </button>
          {exportRecords.isSuccess && (
            <p role="status" className="mt-3 text-sm">
              Archive prepared. Check your downloads.
            </p>
          )}
          {exportRecords.isError && (
            <p role="alert" className="mt-3 text-sm">
              This part didn&apos;t load. Your work is still saved. Try again.
            </p>
          )}
        </section>
        <section aria-labelledby="study-settings-sharing" className="mt-8">
          <h2 id="study-settings-sharing" className="text-xl font-extrabold text-ink">
            Sharing
          </h2>
          <EnglishSharingPanel key={owner} owner={owner} />
        </section>
      </div>
    </StudyShell>
  );
}
